import { cn } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Notification } from "@/hooks/useNotifications";
import { useNotificationTranslation } from "@/hooks/useNotificationTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { getNotificationVisual, notificationTileStyle, resolveNotificationRoute } from "./NotificationItem";
import { formatNotificationTime } from "./notificationTime";

interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  /** Called right before navigation — e.g. to close the parent bottom-sheet. */
  onNavigate?: () => void;
  /**
   * "card": bordered, elevated card used on the notifications page.
   * "compact": flatter row used inside the profile bottom-sheet recent list.
   */
  variant?: "card" | "compact";
  /**
   * Papelera del aviso (auditoría del perito T04-09: en el móvil no había forma de
   * borrar avisos). Sin esto, la tarjeta es la de siempre.
   */
  onDelete?: (id: string) => void;
}

/**
 * Slick, tappable notification used by the new profile bottom-sheet and the
 * notifications page. Visual-only redesign: it reuses the existing type→icon
 * map, translation hook and routing logic from NotificationItem, so the
 * underlying mark-as-read / navigation behaviour is unchanged.
 */
export function NotificationCard({
  notification,
  onMarkAsRead,
  onNavigate,
  variant = "card",
  onDelete,
}: NotificationCardProps) {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { role } = useAuth();
  const { translateNotification } = useNotificationTranslation();

  const config = getNotificationVisual(notification.type);
  const Icon = config.icon;
  const translated = translateNotification(notification);
  const unread = !notification.read;

  const handleClick = () => {
    if (unread) onMarkAsRead(notification.id);
    const route = resolveNotificationRoute(notification, role);
    onNavigate?.();
    if (route) navigate(route);
  };

  const tarjeta = (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "w-full flex items-start gap-3 text-left transition-colors",
        variant === "card"
          ? "p-3.5 rounded-2xl border border-[#b6dded] bg-white shadow-sm hover:bg-blue-50/60 active:scale-[0.99]"
          : "p-3 rounded-xl hover:bg-blue-50/70",
      )}
    >
      <div
        className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white"
        style={notificationTileStyle(config.color)}
      >
        <Icon className="w-5 h-5" />
      </div>

      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-sm leading-snug truncate",
            unread ? "font-bold text-foreground" : "font-semibold text-foreground/90",
          )}
        >
          {translated.title}
        </p>
        {translated.message && (
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
            {translated.message}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground mt-1">
          {formatNotificationTime(notification.created_at, i18n.language, t)}
        </p>
      </div>

      {unread && (
        <span className="shrink-0 mt-1.5 w-2.5 h-2.5 rounded-full bg-primary" aria-hidden />
      )}
    </button>
  );

  if (!onDelete) return tarjeta;
  return (
    <div className="relative group">
      <div className="pr-11">{tarjeta}</div>
      <button
        type="button"
        onClick={() => onDelete(notification.id)}
        aria-label={t("notifications.deleteOne")}
        title={t("notifications.deleteOne")}
        data-testid="notification-delete"
        className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 active:scale-95 transition"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
